import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

//NPM PACKAGES
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { split, isEmpty, round, uniqBy } from "lodash";
import moment from "moment";

//MATERIAL UI COMPONENTS
import CircularProgress from "@material-ui/core/CircularProgress";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import InputAdornment from "@material-ui/core/InputAdornment";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import { styled } from "@mui/material/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import { Add } from "@material-ui/icons";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";

import ProductLists from "./ProductLists";
//core components
import ShippingDetails from "components/ShippingDetails/index";
import ItemAddDialog from "components/OrderDetails/ItemAddOrEdit";
import { getExtraDetails, getOrder, getCarriers, getWarehouses, updateOrder } from "redux/action/order.action";
import { orderShippingStatuses } from "assets/constants/constants";
import { stripeDashboardURL } from "utils";
import { getDisplaySubscriptionValue } from "utils/subscriptionAmount";
import { purchaseOrderStatus } from "assets/constants/constants";
import AddProduct from "../CustomerDetails/AddProduct";
import ExtendSubscriptionDialog from "./ExtendSubscriptionDialog";
import CustomerAppView from "./CustomerAppView";
import axios from "../../config/axios";
import { getProducts } from "redux/action/order.action";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  "&.MuiTableCell-head": {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white
  }
}));

const OrderDetails = () => {
  //CONSTANTS
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const statuses = [
    {
      value: "processing",
      label: t("table.processing")
    },
    {
      value: "confirmed",
      label: t("table.confirmed")
    },
    {
      value: "completed",
      label: t("table.completed")
    },
    {
      value: "cancelled",
      label: t("table.cancelled")
    }
  ];
  const params = useParams();
  const ORDER_STATE = useSelector(state => state.order);
  const [selectedPickupDate, setSelectedPickupDate] = React.useState(null);
  const [selectedPickupTime, setSelectedPickupTime] = React.useState(null);
  const [isOpenAddProductDialog, setIsOpenAddProductDialog] = React.useState(false);
  const [isExtendSubDialogOpen, setIsExtendSubDialogOpen] = React.useState(false);

  const [items, setItems] = React.useState(null);
  const [status, setStatus] = React.useState("");
  const [carrier, setCarrier] = React.useState("");
  const [warehouse, setWarehouse] = React.useState("");
  const [oneTimeValue, setOneTimeValue] = useState(null);
  const [notes, setNotes] = React.useState("");
  const [pickupDays, setPickupDays] = React.useState("");
  const [editingLocation, setEditingLocation] = React.useState(false);
  const [locationForm, setLocationForm] = React.useState({});
  const [updatedOrder, setUpdatedOrder] = React.useState({});
  const { enqueueSnackbar } = useSnackbar();
  const [showComponent, setShowComponent] = React.useState(false);
  const [hooksDependency, setHookDependency] = React.useState(true);
  const [hooksDependency2, setHookDependency2] = React.useState({ random1: true, random2: true, random3: true });
  const [signatureDialogOpen, setSignatureDialogOpen] = React.useState(false);
  const [customerViewOpen, setCustomerViewOpen] = React.useState(false);
  const [internalNoteText, setInternalNoteText] = React.useState("");
  const [internalNoteLoading, setInternalNoteLoading] = React.useState(false);
  const [activityLogs, setActivityLogs] = React.useState([]);
  const [activityLoading, setActivityLoading] = React.useState(false);
  const [activityError, setActivityError] = React.useState(false);
  //FUNCTIONS
  useEffect(() => {
    if (hooksDependency) {
      let { id } = params;
      const handleSuccess = ({ order }) => {
        if (!order.checked_in <= 0 && order.status !== "processing" && !order.carrier_id) {
          enqueueSnackbar(t("messages.carrierWarning"), { variant: "warning" });
        }
      };
      dispatch(getOrder({ id, type: "purchase-order" }, handleSuccess));
      setShowComponent(true);
      if (!ORDER_STATE.extraDetails) {
        dispatch(getExtraDetails());
      }
      if (!ORDER_STATE.carriers || !ORDER_STATE.warehouses) {
        dispatch(getCarriers());
        dispatch(getWarehouses());
      }
      setHookDependency(false);
    }
  }, [ORDER_STATE, dispatch, hooksDependency, params, enqueueSnackbar, t]); //[] ADD DEPENDENCIES ONLY TO REMOVE WARNINGS

  useEffect(() => {
    if (showComponent && ORDER_STATE.order && !selectedPickupDate && hooksDependency2.random1) {
      let date = moment(split(ORDER_STATE.order.pickup_date, "::")[0]).format("YYYY-MM-DD");
      let time = split(ORDER_STATE.order.pickup_date, "::")[1];
      setSelectedPickupDate(date);
      setSelectedPickupTime(time);
      setStatus(ORDER_STATE.order.status);
      setOneTimeValue(ORDER_STATE.order.amount?.one_time?.value ?? 0);
      setNotes(ORDER_STATE.order.notes);
      loadActivityLogs(ORDER_STATE.order._id);
      setPickupDays(ORDER_STATE.order.pickup_days || "");
      setHookDependency2({ ...hooksDependency2, random1: false });
    }
    if (ORDER_STATE.order && ORDER_STATE.carriers && !carrier && hooksDependency2.random2) {
      setCarrier(ORDER_STATE.order.carrier_id);
      setHookDependency2({ ...hooksDependency2, random2: false });
    }
    if (ORDER_STATE.order && ORDER_STATE.warehouses && !warehouse && hooksDependency2.random3) {
      setWarehouse(ORDER_STATE.order.warehouse_id);
      setHookDependency2({ ...hooksDependency2, random3: false });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ORDER_STATE, hooksDependency2, carrier, selectedPickupDate, showComponent, warehouse]); //[ORDER_STATE] OTHER ADDED TO REMOVE WARNING

  const handleupdateOrderSuccess = () => {
    enqueueSnackbar(t("messages.orderUpdated"), { variant: "success" });
    if (updatedOrder?.status === "confirmed" && !carrier) {
      enqueueSnackbar(t("messages.carrierWarning"), { variant: "warning" });
    }
    setUpdatedOrder({});
  };
  const handleUpdateOrderFailure = ({ error }) => {
    enqueueSnackbar(error, { variant: "error" });
  };
  useEffect(() => {
    if (!ORDER_STATE.products) {
      dispatch(getProducts());
    }
  }, [ORDER_STATE.products, dispatch]);

  const handleSaveLocation = () => {
    dispatch(
      updateOrder(
        { updatedOrder: { location: locationForm }, order_id: ORDER_STATE.order._id, type: "purchase-order" },
        () => { setEditingLocation(false); enqueueSnackbar("Morada actualizada", { variant: "success" }); },
        () => { enqueueSnackbar("Erro ao guardar morada", { variant: "error" }); }
      )
    );
  };

  const HandleSubmit = event => {
    event.preventDefault();

    let order = { ...updatedOrder };
    if (order.date && order.time) {
      order.pickup_date = `${order.date}::${order.time}`;
      delete order.date;
      delete order.time;
    } else if (order.date) {
      order.pickup_date = `${order.date}::${split(ORDER_STATE.order.pickup_date, "::")[1]}`;
      delete order.date;
    } else if (order.time) {
      order.pickup_date = `${split(ORDER_STATE.order.pickup_date, "::")[0]}::${order.time}`;
      delete order.time;
    }
    dispatch(
      updateOrder(
        {
          updatedOrder: order,
          order_id: ORDER_STATE.order._id,
          type: "purchase-order"
        },
        handleupdateOrderSuccess,
        handleUpdateOrderFailure
      )
    );
  };
  const handlePikupDateChange = event => {
    // Hoje ou datas futuras (antes, o próprio dia era recusado sem aviso)
    if (moment(event.target.value).isSameOrAfter(moment(), "day")) {
      setSelectedPickupDate(event.target.value);
      setUpdatedOrder({ ...updatedOrder, date: moment(event.target.value).format("YYYY-MM-DD") });
    } else {
      enqueueSnackbar("A data de recolha não pode ser no passado.", { variant: "warning" });
    }
  };
  const handlePickupTimeChange = event => {
    setSelectedPickupTime(event.target.value);
    setUpdatedOrder({ ...updatedOrder, time: event.target.value });
  };

  const onChangeOneTimeValue = value => {
    setOneTimeValue(value);
    setUpdatedOrder({ ...updatedOrder, one_time: { value, currency: "€" } });
  };
  const onOpenItems = items => {
    setItems({ items });
  };
  const onChangeCarrier = value => {
    setCarrier(value);
    setUpdatedOrder({ ...updatedOrder, carrier_id: value });
  };
  const onChangeWarehouse = value => {
    setWarehouse(value);
    setUpdatedOrder({ ...updatedOrder, warehouse_id: value });
  };
  const onnotesChange = event => {
    setNotes(event.target.value);
    setUpdatedOrder({ ...updatedOrder, notes: event.target.value });
  };
  const loadActivityLogs = React.useCallback(async (orderId) => {
    if (!orderId) return;
    setActivityLoading(true);
    setActivityError(false);
    try {
      const res = await axios.get(`/admin/orders/${orderId}/activity`);
      setActivityLogs(res.data?.data || []);
    } catch {
      setActivityError(true);
    }
    finally { setActivityLoading(false); }
  }, []);

  const handleAddInternalNote = async () => {
    if (!internalNoteText.trim()) return;
    setInternalNoteLoading(true);
    try {
      await axios.post(`/admin/orders/${ORDER_STATE.order._id}/notes`, {
        text: internalNoteText,
        author: "Admin",
      });
      setInternalNoteText("");
      dispatch(getOrder({ id: ORDER_STATE.order._id, type: "purchase-order" }));
      loadActivityLogs(ORDER_STATE.order._id);
    } catch (e) {
      enqueueSnackbar("Erro ao adicionar nota", { variant: "error" });
    } finally {
      setInternalNoteLoading(false);
    }
  };

  const handleDeleteInternalNote = async (noteId) => {
    setInternalNoteLoading(true);
    try {
      await axios.delete(`/admin/orders/${ORDER_STATE.order._id}/notes/${noteId}`);
      dispatch(getOrder({ id: ORDER_STATE.order._id, type: "purchase-order" }));
    } catch (e) {
      enqueueSnackbar("Erro ao apagar nota", { variant: "error" });
    } finally {
      setInternalNoteLoading(false);
    }
  };

  const handleCloseItemDialog = () => {
    setItems(null);
  };
  const handleCloseSuccessItemDialog = () => {
    let { id } = params;
    setItems(null);
    dispatch(getOrder({ id, type: "purchase-order" }));
    enqueueSnackbar(t("messages.itemAdded"), { variant: "success" });
  };
  const handleStatusChange = event => {
    setStatus(event.target.value);
    setUpdatedOrder({ ...updatedOrder, status: event.target.value });
  };

  const handlePickupDaysChange = (event) => {
    const val = event.target.value;
    setPickupDays(val);
    setUpdatedOrder({ ...updatedOrder, pickup_days: val });
  };

  return (
    <div>
      {items && (
        <ItemAddDialog
          items={items}
          orderStatus={ORDER_STATE.order?.status}
          handleCloseItemDialog={handleCloseItemDialog}
          handleCloseSuccessItemDialog={handleCloseSuccessItemDialog}
          orderId={ORDER_STATE.order._id}
        />
      )}

      <ExtendSubscriptionDialog
        open={isExtendSubDialogOpen}
        onClose={() => setIsExtendSubDialogOpen(false)}
        orderId={ORDER_STATE?.order?._id}
      />
      {isOpenAddProductDialog && (
        <AddProduct
          setIsOpenAddProductDialog={setIsOpenAddProductDialog}
          allowOrderSelection={false}
          orderId={ORDER_STATE?.order?._id}
          userId={ORDER_STATE?.order?.user_id?._id}
          priceVersion={ORDER_STATE?.order?.price_version || ORDER_STATE?.order?.user_id?.price_version || ORDER_STATE?.products?.[ORDER_STATE?.products?.length - 1]?.price_version}
        />
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <h3 style={{ fontWeight: "600", margin: 0 }}>{t("orders.orderDetails")}</h3>
        {ORDER_STATE.order && (
          <Button
            variant="outlined"
            onClick={() => setCustomerViewOpen(true)}
            style={{ borderColor: '#046333', color: '#046333' }}
          >
            <span role="img" aria-label="olho">👁️</span>&nbsp;Ver como cliente
          </Button>
        )}
      </div>

      {ORDER_STATE.fetchingOrderLoading ? (
        <div className="loading">
          <CircularProgress />
        </div>
      ) : (
        <div>
          {(ORDER_STATE.fetchingOrderError ||
            ORDER_STATE.fetchingExtraDetailsError ||
            ORDER_STATE.fetchingCarriersError ||
            ORDER_STATE.fetchingWarehousesError ||
            ORDER_STATE.order === undefined) && (
            <div style={{ margin: "12px 0", padding: "12px 16px", background: "#fdecea", color: "#b71c1c", borderRadius: 6 }}>
              Não foi possível carregar este pedido (pode não existir ou a ligação falhou). Recarrega a página; se o erro continuar, avisa a equipa técnica.
            </div>
          )}
          {showComponent && ORDER_STATE.order && ORDER_STATE.extraDetails && ORDER_STATE.carriers && ORDER_STATE.warehouses && (
            <div>
              <div className="details">
                <div className="title">
                  {t("orders.orderDetails")} #{ORDER_STATE.order._id}
                </div>
                <div className="card">
                  <div className="col">
                    <div className="row">
                      <div className="label">{t("table.subscribedAmount")}</div>
                      <div>
                        {getDisplaySubscriptionValue(
                          ORDER_STATE.order.amount?.recurring?.value,
                          ORDER_STATE.order.payment_frequency,
                          ORDER_STATE.order.total_m2 ?? ORDER_STATE.order.current_m2
                        )}{" "}
                        {ORDER_STATE.order.amount?.recurring?.currency ?? "€"}
                      </div>
                    </div>

                    {ORDER_STATE.order?.pickup_paused && (
                      <div className="row">
                        <div className="label">Estado da Recolha</div>
                        <div className="form-input">
                          <Chip
                            label={"⏸ Pausada — continua em " + (ORDER_STATE.order?.pickup_resume_date || "amanhã")}
                            style={{ backgroundColor: "#FFF3CD", color: "#856404", fontWeight: 600 }}
                          />
                        </div>
                      </div>
                    )}
                    <div className="row">
                      <div className="label">{t("orders.pickupDate")}</div>
                      <div className="form-input">
                        <TextField
                          id="datetime-local"
                          label={t("table.date")}
                          type="date"
                          variant="outlined"
                          InputLabelProps={{
                            shrink: true
                          }}
                          value={selectedPickupDate === null ? "" : selectedPickupDate}
                          onChange={handlePikupDateChange}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="label">{t("orders.pickupTime")}</div>
                      <div className="form-input">
                        <FormControl variant="outlined">
                          <InputLabel id="demo-simple-select-outlined-label">{t("orders.pickupTime")}</InputLabel>
                          <Select
                            labelId="demo-simple-select-outlined-label"
                            id="demo-simple-select-outlined"
                            value={selectedPickupTime === null ? "" : selectedPickupTime}
                            onChange={handlePickupTimeChange}
                            label="Time"
                          >
                            <MenuItem value="08h - 12h">{t("time_slot_morning", "08h - 12h")}</MenuItem>
                            <MenuItem value="13h - 17h">{t("time_slot_afternoon", "13h - 17h")}</MenuItem>
                          </Select>
                        </FormControl>
                      </div>
                    </div>
                    <div className="row">
                      <div className="label">{t("orders.statusLabel")}</div>
                      <div className="form-input">
                        <TextField
                          id="standard-select-status"
                          select
                          label={t("common.select", "Select")}
                          value={status === null ? "" : status}
                          onChange={e => handleStatusChange(e)}
                          helperText={''}
                          variant="outlined"
                        >
                          {statuses.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </div>
                    </div>
                    <div className="row">
                      <div className="label">{t("orderDetails.priceVersion")}</div>
                      <div>{ORDER_STATE.order?.price_version ?? ORDER_STATE.order?.user_id?.price_version}</div>
                    </div>
                    <div className="row">
                      <div className="label">Dias de Recolha</div>
                      <div className="form-input">
                        <TextField
                          id="pickup-days-select"
                          select
                          label="Seleccionar"
                          value={pickupDays}
                          onChange={handlePickupDaysChange}
                          variant="outlined"
                        >
                          <MenuItem value="">—</MenuItem>
                          <MenuItem value="1">1 dia</MenuItem>
                          <MenuItem value="2+">2 ou mais dias</MenuItem>
                        </TextField>
                      </div>
                    </div>
                    {ORDER_STATE.order?.signature_image_url && (
                      <div className="row">
                        <div className="label">{t("orderDetails.signature", "Signature")}</div>
                        <div>
                          <img
                            src={ORDER_STATE.order.signature_image_url}
                            alt={t("orderDetails.signatureAlt", "Customer signature")}
                            style={{ maxWidth: "300px", maxHeight: "150px", border: "1px solid #ddd", borderRadius: "4px", padding: "5px", cursor: "pointer" }}
                            onClick={() => setSignatureDialogOpen(true)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="col">
                    <div className="row" style={{ display: "flex", flexDirection: "column" }}>
                      <div className="row">
                        <div className="label" style={{ minWidth: "100px" }}>
                          {t("orderDetails.location")}
                          <button
                            type="button"
                            onClick={() => {
                              setLocationForm({
                                address: ORDER_STATE.order.location?.address || "",
                                floor: ORDER_STATE.order.location?.floor || "",
                                city: ORDER_STATE.order.location?.city || "",
                                zipcode: ORDER_STATE.order.location?.zipcode || ""
                              });
                              setEditingLocation(true);
                            }}
                            style={{ marginLeft: "8px", background: "none", border: "none", cursor: "pointer", fontSize: "14px", color: "#666" }}
                            title="Editar morada"
                          ><span role="img" aria-label="Editar morada">✏️</span></button>
                        </div>
                        <div>
                          {editingLocation ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "4px" }}>
                              <TextField size="small" variant="outlined" label={t("orderDetails.address")} value={locationForm.address || ""} onChange={e => setLocationForm({ ...locationForm, address: e.target.value })} style={{ maxWidth: "320px" }} />
                              <TextField size="small" variant="outlined" label={t("orderDetails.floor")} value={locationForm.floor || ""} onChange={e => setLocationForm({ ...locationForm, floor: e.target.value })} style={{ maxWidth: "320px" }} />
                              <TextField size="small" variant="outlined" label={t("orderDetails.city")} value={locationForm.city || ""} onChange={e => setLocationForm({ ...locationForm, city: e.target.value })} style={{ maxWidth: "320px" }} />
                              <TextField size="small" variant="outlined" label={t("orderDetails.zipcode")} value={locationForm.zipcode || ""} onChange={e => setLocationForm({ ...locationForm, zipcode: e.target.value })} style={{ maxWidth: "320px" }} />
                              <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
                                <Button size="small" variant="contained" color="primary" onClick={handleSaveLocation}>Guardar</Button>
                                <Button size="small" variant="outlined" onClick={() => setEditingLocation(false)}>Cancelar</Button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className="row">
                                <div className="label">{t("orderDetails.address")}</div>
                                <div>{ORDER_STATE.order.location?.address}</div>
                              </div>
                              <div className="row">
                                <div className="label">{t("orderDetails.floor")}</div>
                                <div>{ORDER_STATE.order.location?.floor}</div>
                              </div>
                              <div className="row">
                                <div className="label">{t("orderDetails.city")}</div>
                                <div>{ORDER_STATE.order.location?.city}</div>
                              </div>
                              <div className="row">
                                <div className="label">{t("orderDetails.zipcode")}</div>
                                <div>{ORDER_STATE.order.location?.zipcode}</div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="row">
                        <div className="label" style={{ minWidth: "100px" }}>
                          {t("orderDetails.notes")}
                        </div>
                        <div style={{ width: "100%", maxWidth: "400px" }}>
                          <TextField
                            id="outlined-multiline-static"
                            label={t("orderDetails.multiline")}
                            multiline
                            rows={4}
                            onChange={e => onnotesChange(e)}
                            value={notes || ""}
                            variant="outlined"
                            fullWidth={true}
                          />
                        </div>
                      </div>
                      {/* // */}
                    </div>
                  </div>
                </div>
                {/* Detalhes do calculator — divisao personalizada e descricao livre */}
                {(ORDER_STATE.order?.calculator?.customCategoryName || ORDER_STATE.order?.calculator?.otherDescription) && (
                  <div className="card" style={{ margin: "16px 0", padding: "16px", backgroundColor: "#FFF3CD", borderRadius: "8px", border: "2px solid #F59E0B" }}>
                    <h4 style={{ color: "#92400E", marginBottom: "12px", fontSize: "14px", fontWeight: "700" }}>
                      <span role="img" aria-label="aviso">⚠️</span> Contactar cliente — Itens personalizados ("Outros")
                    </h4>
                    <p style={{ fontSize: "12px", color: "#92400E", marginBottom: "10px" }}>
                      O cliente usou a categoria "Outros". O espaço necessário deve ser confirmado antes da recolha.
                    </p>
                    {ORDER_STATE.order?.calculator?.customCategoryName && (
                      <div style={{ marginBottom: "8px" }}>
                        <span style={{ fontSize: "13px", color: "#666", fontWeight: "600" }}>Divisão: </span>
                        <span style={{ fontSize: "13px", color: "#25447D", fontWeight: "700" }}>
                          {ORDER_STATE.order.calculator.customCategoryName}
                        </span>
                      </div>
                    )}
                    {ORDER_STATE.order?.calculator?.otherDescription && (
                      <div style={{ marginBottom: "10px" }}>
                        <span style={{ fontSize: "13px", color: "#666", fontWeight: "600" }}>Itens descritos: </span>
                        <span style={{ fontSize: "13px", color: "#1A2B4A" }}>
                          {ORDER_STATE.order.calculator.otherDescription}
                        </span>
                      </div>
                    )}
                    {ORDER_STATE.order?.calculator?.customPhoto && (
                      <div style={{ marginTop: "10px" }}>
                        <span style={{ fontSize: "13px", color: "#666", fontWeight: "600", display: "block", marginBottom: "6px" }}>Foto enviada pelo cliente:</span>
                        <img
                          src={ORDER_STATE.order.calculator.customPhoto}
                          alt="Foto itens personalizados"
                          style={{ maxWidth: "280px", maxHeight: "200px", borderRadius: "8px", objectFit: "cover", border: "1px solid #F59E0B", cursor: "pointer" }}
                          onClick={() => window.open(ORDER_STATE.order.calculator.customPhoto, "_blank")}
                        />
                      </div>
                    )}
                  </div>
                )}
                {/* Conteúdo interior — itens marcados com "Sim" pelo cliente */}
                {ORDER_STATE.order?.calculator?.itemContents && Object.values(ORDER_STATE.order.calculator.itemContents).some(v => v === true) && (
                  <div className="card" style={{ margin: "16px 0", padding: "16px", backgroundColor: "#FEF3C7", borderRadius: "8px", border: "1px solid #F59E0B" }}>
                    <h4 style={{ color: "#92400E", marginBottom: "12px", fontSize: "14px", fontWeight: "700" }}>
                      <span role="img" aria-label="aviso">⚠️</span> Móveis com Conteúdo Interior
                    </h4>
                    <p style={{ fontSize: "12px", color: "#92400E", marginBottom: "10px" }}>
                      O cliente indicou que os seguintes itens têm conteúdo no interior. Verificar na recolha / avisar para esvaziar antes.
                    </p>
                    <ul style={{ margin: 0, paddingLeft: "16px" }}>
                      {ORDER_STATE.order?.calculator?.selectedItems && Object.values(ORDER_STATE.order.calculator.selectedItems)
                        .filter(item => ORDER_STATE.order.calculator.itemContents[item.id] === true)
                        .map((item, idx) => (
                          <li key={idx} style={{ fontSize: "13px", color: "#1A2B4A", marginBottom: "4px" }}>
                            <strong>{item.name}</strong>
                            {item.quantity > 1 && <span style={{ color: "#666" }}> × {item.quantity}</span>}
                          </li>
                        ))
                      }
                    </ul>
                  </div>
                )}
                <div className="save">
                  {ORDER_STATE.updateOrderLoading ? (
                    <Button variant="contained" color="primary" disabled={true}>
                      {t("common.loading")}
                    </Button>
                  ) : (
                    <Button variant="contained" color="primary" onClick={e => HandleSubmit(e)} disabled={Object.keys(updatedOrder).length <= 0 ? true : false}>
                      {t("common.save")}
                    </Button>
                  )}
                </div>
              </div>

              <div className="details">
                <div className="title">{t("orderDetails.shippingDetails")}</div>
                <div className="card">
                  <div className="col">
                    <div className="row">
                      <div className="label">{t("orderDetails.shippingCost")}</div>
                      <div className="form-input">
                        <FormControl fullWidth variant="outlined">
                          <InputLabel htmlFor="outlined-adornment-amount">{t("orderDetails.amount")}</InputLabel>
                          <OutlinedInput
                            id="outlined-adornment-amount"
                            value={oneTimeValue === null ? "" : round(oneTimeValue, 2)}
                            onChange={e => {
                              onChangeOneTimeValue(e.target.value);
                            }}
                            disabled
                            startAdornment={<InputAdornment position="start">€</InputAdornment>}
                            labelWidth={60}
                          />
                        </FormControl>
                      </div>
                    </div>
                    {ORDER_STATE.order.stripe_shipping_details && (
                      <div className="row">
                        <div className="label">{t("orderDetails.shippingStatus")}</div>
                        <div>
                          <Chip
                            label={orderShippingStatuses[ORDER_STATE.order.stripe_shipping_details.p_intent_status]?.name}
                            variant="outlined"
                            style={{
                              border: "1px solid",
                              borderColor: orderShippingStatuses[ORDER_STATE.order.stripe_shipping_details.p_intent_status]?.borderColor,
                              backgroundColor: orderShippingStatuses[ORDER_STATE.order.stripe_shipping_details.p_intent_status]?.backgroundColor,
                              borderRadius: "5px",
                              fontWeight: "bold",
                              color: orderShippingStatuses[ORDER_STATE.order.stripe_shipping_details.p_intent_status]?.color
                            }}
                          />
                        </div>
                      </div>
                    )}
                    <div className="row">
                      <div className="label">{t("orderDetails.carrier")}</div>

                      <div className="form-input">
                        <TextField
                          id="standard-select-status"
                          select
                          fullWidth={true}
                          label={t("orderDetails.select")}
                          disabled={ORDER_STATE.order.status === "completed" || ORDER_STATE.order.status === "cancelled"}
                          value={carrier === null ? "" : carrier}
                          onChange={e => onChangeCarrier(e.target.value)}
                          helperText={t("messages.pleaseSelectCarrier")}
                          variant="outlined"
                        >
                          {ORDER_STATE.carriers.map(option => (
                            <MenuItem key={option._id} value={option._id}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </div>
                    </div>
                    <div className="row">
                      <div className="label">{t("orderDetails.warehouse")}</div>
                      <div className="form-input">
                        <TextField
                          id="standard-select-status"
                          select
                          fullWidth={true}
                          label={t("orderDetails.select")}
                          value={warehouse === null ? "" : warehouse}
                          onChange={e => onChangeWarehouse(e.target.value)}
                          helperText={t("messages.pleaseSelectWarehouse")}
                          variant="outlined"
                        >
                          {ORDER_STATE.warehouses.map(option => (
                            <MenuItem key={option._id} value={option._id}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </div>
                    </div>
                    {ORDER_STATE.order.location && ORDER_STATE.order.location.floor && (
                      <div className="row">
                        <div className="label">{t("orderDetails.floor")}</div>
                        <div style={{ paddingTop: "8px", fontWeight: "500" }}>
                          {ORDER_STATE.order.location.floor}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="col">
                    <ShippingDetails
                      listOfExtraDetails={ORDER_STATE.order.list_of_extra_details}
                      elevatorFloorNumber={
                        ORDER_STATE.order.elevator_floor_number ||
                        (() => {
                          // Encomendas antigas: o andar só ficou no custo "Andares sem elevador"
                          let fees = ORDER_STATE.order.one_time_fee_details;
                          if (typeof fees === "string") {
                            try { fees = JSON.parse(fees); } catch (e) { fees = null; }
                          }
                          const item = (fees?.items || []).find(i => i.id === "elevator_floor_cost");
                          return item?.metadata?.floor_number || null;
                        })()
                      }
                    />
                    {ORDER_STATE.order.personal_boxes && ORDER_STATE.order.personal_boxes.quantity > 0 && (
                      <div style={{ marginTop: "20px" }}>
                        <div className="label"><span role="img" aria-label="caixa">📦</span> Caixas Pessoais do Cliente</div>
                        <div className="row">
                          <div style={{ minWidth: "325px", maxWidth: "325px" }}>Quantidade de caixas</div>
                          <div style={{ paddingTop: "8px", fontWeight: "500" }}>
                            {ORDER_STATE.order.personal_boxes.quantity}
                          </div>
                        </div>
                        <div className="row">
                          <div style={{ minWidth: "325px", maxWidth: "325px" }}>Tamanho das caixas</div>
                          <div style={{ paddingTop: "8px", fontWeight: "500" }}>
                            {(() => {
                               const s = ORDER_STATE.order.personal_boxes.size;
                               if (s === 'normal') return 'Caixa Normal (40×40×40 cm)';
                               if (s === 'grande') return 'Caixa Grande (60×40×40 cm)';
                               return 'Caixa Normal (40×40×40 cm)';
                             })()}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {ORDER_STATE.order.services && Array.isArray(ORDER_STATE.order.services) && ORDER_STATE.order.services.length > 0 && (
                <div className="details">
                  <div className="title">{t("orderDetails.servicesSectionTitle")}</div>
                  <div className="card">
                    <TableContainer component={Paper} style={{ boxShadow: "none", border: "1px solid #e0e0e0" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <StyledTableCell>{t("orderDetails.name")}</StyledTableCell>
                            <StyledTableCell align="right">{t("orderDetails.quantity")}</StyledTableCell>
                            <StyledTableCell align="right">{t("orderDetails.unitPrice")}</StyledTableCell>
                            <StyledTableCell>{t("orderDetails.description")}</StyledTableCell>
                            <StyledTableCell align="right">{t("orderDetails.subtotal")}</StyledTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {ORDER_STATE.order.services.map((s, idx) => {
                            const subtotal = round((s.quantity || 0) * (Number(s.unitPrice) || 0), 2);
                            return (
                              <TableRow key={s.id || idx}>
                                <TableCell>{s.name || "-"}</TableCell>
                                <TableCell align="right">{s.quantity ?? "-"}</TableCell>
                                <TableCell align="right">€{round(Number(s.unitPrice) || 0, 2)}</TableCell>
                                <TableCell>{s.description || "-"}</TableCell>
                                <TableCell align="right">€{subtotal}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                </div>
              )}

              <div className="details">
                <div className="title">{t("orderDetails.costBreakdown")}</div>
                <div className="card">
                  <div className="col">
                    {(() => {
                      let feeDetails = ORDER_STATE.order.one_time_fee_details;
                      if (typeof feeDetails === 'string') {
                        try {
                          feeDetails = JSON.parse(feeDetails);
                        } catch (e) {
                          feeDetails = null;
                        }
                      }
                      return feeDetails && (
                        <>
                          <div className="row">
                            <div className="label">{t("orderDetails.oneTimeTotal")}</div>
                            <div>
                              {round(ORDER_STATE.order.amount?.one_time?.value ?? 0, 2)} {ORDER_STATE.order.amount?.one_time?.currency ?? "€"}
                            </div>
                          </div>
                          {feeDetails.items && Array.isArray(feeDetails.items) && feeDetails.items.length > 0 && (
                          <div style={{ marginTop: "20px" }}>
                            <div className="label" style={{ marginBottom: "16px", fontWeight: "600", fontSize: "1.1em" }}>
                              {t("orderDetails.carrierCharges", "Cobranças Adicionais")}
                            </div>
                            <div style={{ border: "1px solid #e0e0e0", borderRadius: "4px", padding: "0" }}>
                              {feeDetails.items.map((item, index) => {
                                const itemName = item.namePt || item.name || item.nameEn || "-";
                                const itemDescription = item.descriptionPt || item.description || "";
                                const quantity = item.quantity || 1;
                                const unitPrice = item.price || item.unitPrice || 0;
                                const subtotal = item.subtotal || (quantity * unitPrice);
                                const currency = item.currency || "€";
                                const isExtraHours = item.id === "extra_hours" || item.type === "extra_hours" || 
                                                   item.description?.toLowerCase().includes("hora") ||
                                                   item.descriptionPt?.toLowerCase().includes("hora") ||
                                                   item.name?.toLowerCase().includes("hora") ||
                                                   item.namePt?.toLowerCase().includes("hora");
                                
                                return (
                                  <div 
                                    key={index}
                                    style={{
                                      padding: "16px",
                                      borderBottom: index < feeDetails.items.length - 1 ? "1px solid #e0e0e0" : "none",
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start"
                                    }}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontWeight: "600", marginBottom: "4px", fontSize: "0.95em" }}>
                                        {itemName}
                                      </div>
                                      {isExtraHours && item.metadata?.extra_hours && item.metadata?.extra_hour_price ? (
                                        <div style={{ fontSize: "0.85em", color: "#666", marginTop: "2px" }}>
                                          {round(item.metadata.extra_hours, 2)} {t("orderDetails.hours", "horas")} × {round(item.metadata.extra_hour_price, 2)}{currency}/{t("orderDetails.hour", "hora")}
                                        </div>
                                      ) : (
                                        <div style={{ fontSize: "0.85em", color: "#666", marginTop: "2px" }}>
                                          {quantity} × {round(unitPrice, 2)}{currency}
                                        </div>
                                      )}
                                      {itemDescription && !isExtraHours && (
                                        <div style={{ fontSize: "0.85em", color: "#666", marginTop: "4px" }}>
                                          {itemDescription}
                                        </div>
                                      )}
                                      {item.metadata && !isExtraHours && (
                                        <div style={{ fontSize: "0.85em", color: "#666", marginTop: "4px" }}>
                                          {item.metadata.flat_pickup_cost && (
                                            <div>{t("orderDetails.flatPickupCost")}: {round(item.metadata.flat_pickup_cost, 2)}€</div>
                                          )}
                                          {item.metadata.extra_distance_cost && (
                                            <div>{t("orderDetails.extraDistanceCost")}: {round(item.metadata.extra_distance_cost, 2)}€</div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    <div style={{ fontWeight: "600", fontSize: "0.95em", marginLeft: "16px" }}>
                                      {round(subtotal, 2)}{currency}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                      );
                    })()}
                    {(ORDER_STATE.order.pickup_duration_hours || ORDER_STATE.order.pickup_extra_charge_amount) && (
                      <div style={{ marginTop: "24px", borderTop: "1px solid #e0e0e0", paddingTop: "16px" }}>
                        <div className="label" style={{ marginBottom: "12px", fontWeight: "600", fontSize: "0.95em", color: "#424242" }}>
                          {t("orderDetails.pickupDetails", "Pickup Details")}
                        </div>
                        {ORDER_STATE.order.pickup_duration_hours && (
                          <div className="row" style={{ marginBottom: "8px" }}>
                            <div className="label" style={{ minWidth: "180px" }}>{t("orderDetails.pickupDurationHours")}</div>
                            <div style={{ fontWeight: "500" }}>{round(ORDER_STATE.order.pickup_duration_hours, 2)} {t("orderDetails.hours", "hours")}</div>
                          </div>
                        )}
                        {ORDER_STATE.order.pickup_extra_charge_amount && (
                          <div className="row" style={{ marginBottom: "8px" }}>
                            <div className="label" style={{ minWidth: "180px" }}>{t("orderDetails.extraCharge")}</div>
                            <div style={{ fontWeight: "600" }}>{round(ORDER_STATE.order.pickup_extra_charge_amount, 2)}€</div>
                          </div>
                        )}
                        {ORDER_STATE.order.pickup_extra_charge_payment_intent_id && (
                          <div className="row">
                            <div className="label" style={{ minWidth: "180px" }}>{t("orderDetails.extraChargePaymentIntent")}</div>
                            <div style={{ wordBreak: "break-all", fontSize: "0.9em", color: "#666" }}>
                              {ORDER_STATE.order.pickup_extra_charge_payment_intent_id}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="col">
                    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                      {(ORDER_STATE.order.current_m2 !== null && ORDER_STATE.order.current_m2 !== undefined) || 
                       (ORDER_STATE.order.total_m2 !== null && ORDER_STATE.order.total_m2 !== undefined) ? (
                        <div style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: "16px" }}>
                          <div style={{ fontWeight: "600", marginBottom: "12px", fontSize: "0.95em", color: "#424242" }}>
                            {t("orderDetails.storageInfo", "Storage Information")}
                          </div>
                          {ORDER_STATE.order.current_m2 !== null && ORDER_STATE.order.current_m2 !== undefined && (
                            <div className="row" style={{ marginBottom: "12px" }}>
                              <div className="label" style={{ minWidth: "140px", marginRight: "16px" }}>{t("orderDetails.currentM2")} </div>
                              <div style={{ fontWeight: "500" }}>{round(ORDER_STATE.order.current_m2, 1)} m²</div>
                            </div>
                          )}
                          {ORDER_STATE.order.total_m2 !== null && ORDER_STATE.order.total_m2 !== undefined && (
                            <div className="row" style={{ marginBottom: "12px" }}>
                              <div className="label" style={{ minWidth: "140px", marginRight: "16px" }}>{t("orderDetails.totalM2")}</div>
                              <div style={{ fontWeight: "500" }}>{round(ORDER_STATE.order.total_m2, 1)} m²</div>
                            </div>
                          )}
                        </div>
                      ) : null}

                      {(ORDER_STATE.order.payment_frequency || ORDER_STATE.order.size_calculation_method) && (
                        <div style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: "16px" }}>
                          <div style={{ fontWeight: "600", marginBottom: "12px", fontSize: "0.95em", color: "#424242" }}>
                            {t("orderDetails.orderSettings", "Order Settings")}
                          </div>
                          {ORDER_STATE.order.payment_frequency && (
                            <div className="row" style={{ marginBottom: "8px" }}>
                              <div className="label" style={{ minWidth: "140px" }}>{t("orderDetails.paymentFrequency")}</div>
                              <Chip
                                label={ORDER_STATE.order.payment_frequency.charAt(0).toUpperCase() + ORDER_STATE.order.payment_frequency.slice(1)}
                                size="small"
                                style={{ backgroundColor: "#e3f2fd", color: "#1976d2", fontWeight: "500" }}
                              />
                            </div>
                          )}
                          {ORDER_STATE.order.size_calculation_method && (
                            <div className="row">
                              <div className="label" style={{ minWidth: "140px" }}>{t("orderDetails.sizeCalculationMethod")}</div>
                              <Chip
                                label={ORDER_STATE.order.size_calculation_method.toUpperCase()}
                                size="small"
                                style={{ 
                                  backgroundColor: ORDER_STATE.order.size_calculation_method === "ai" ? "#e8f5e9" : "#fff3e0",
                                  color: ORDER_STATE.order.size_calculation_method === "ai" ? "#2e7d32" : "#e65100",
                                  fontWeight: "500"
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {ORDER_STATE.order.coupon_code && (
                        <div style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: "16px" }}>
                          <div style={{ fontWeight: "600", marginBottom: "12px", fontSize: "0.95em", color: "#424242" }}>
                            {t("orderDetails.discount", "Discount")}
                          </div>
                          <div className="row">
                            <div className="label" style={{ minWidth: "140px" }}>{t("orderDetails.couponCode")}</div>
                            <Chip
                              label={ORDER_STATE.order.coupon_code}
                              size="small"
                              style={{ backgroundColor: "#f3e5f5", color: "#7b1fa2", fontWeight: "500" }}
                            />
                          </div>
                        </div>
                      )}

                      {ORDER_STATE.order.analytics && (ORDER_STATE.order.analytics.utm_source || ORDER_STATE.order.analytics.utm_medium) && (
                        <div style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: "16px" }}>
                          <div style={{ fontWeight: "600", marginBottom: "12px", fontSize: "0.95em", color: "#424242" }}>
                            {t("orderDetails.analytics", "Analytics")}
                          </div>
                          {ORDER_STATE.order.analytics.utm_source && (
                            <div className="row" style={{ marginBottom: "8px" }}>
                              <div className="label" style={{ minWidth: "140px" }}>{t("orderDetails.utmSource")}</div>
                              <div style={{ fontWeight: "500" }}>{ORDER_STATE.order.analytics.utm_source}</div>
                            </div>
                          )}
                          {ORDER_STATE.order.analytics.utm_medium && (
                            <div className="row">
                              <div className="label" style={{ minWidth: "140px" }}>{t("orderDetails.utmMedium")}</div>
                              <div style={{ fontWeight: "500" }}>{ORDER_STATE.order.analytics.utm_medium}</div>
                            </div>
                          )}
                        </div>
                      )}

                      <div style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: "16px" }}>
                        <div style={{ fontWeight: "600", marginBottom: "12px", fontSize: "0.95em", color: "#424242" }}>
                          {t("orderDetails.orderFlags", "Order Flags")}
                        </div>
                        <div className="row" style={{ marginBottom: "8px" }}>
                          <div className="label" style={{ minWidth: "140px" }}>{t("orderDetails.isFirstOrder")}</div>
                          <Chip
                            label={ORDER_STATE.order.is_first_order ? t("common.yes", "Yes") : t("common.no", "No")}
                            size="small"
                            style={{
                              backgroundColor: ORDER_STATE.order.is_first_order ? "#e8f5e9" : "#fafafa",
                              color: ORDER_STATE.order.is_first_order ? "#2e7d32" : "#757575",
                              fontWeight: "500"
                            }}
                          />
                        </div>
                        <div className="row">
                          <div className="label" style={{ minWidth: "140px" }}>{t("orderDetails.subscriptionUpgrade")}</div>
                          <Chip
                            label={ORDER_STATE.order.subscription_upgrade ? t("common.yes", "Yes") : t("common.no", "No")}
                            size="small"
                            style={{
                              backgroundColor: ORDER_STATE.order.subscription_upgrade ? "#e8f5e9" : "#fafafa",
                              color: ORDER_STATE.order.subscription_upgrade ? "#2e7d32" : "#757575",
                              fontWeight: "500"
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="details">
                <div className="title">{t("orderDetails.customerInformation")}</div>

                {ORDER_STATE.order.user_id ? (
                <div className="col">
                  <div className="row">
                    <div className="label">{t("table.id")}</div>

                    <Link to={`/admin/customers/${ORDER_STATE.order.user_id._id}`}>
                      <div style={{ fontWeight: "400" }}>{ORDER_STATE.order.user_id._id}</div>
                    </Link>
                  </div>
                  <div className="row">
                    <div className="label">{t("orderDetails.subscriptionId")}</div>
                    <a href={`${stripeDashboardURL}/subscriptions/${ORDER_STATE.order.user_id.stripe_subscription_id}`} target="_blank" rel="noopener noreferrer">
                      <div style={{ fontWeight: "400" }}>{ORDER_STATE.order.user_id.stripe_subscription_id}</div>
                    </a>
                  </div>
                  <div className="row">
                    <div className="label">{t("orderDetails.name")}</div>

                    <div>{ORDER_STATE.order.user_id.name}</div>
                  </div>
                  <div className="row">
                    <div className="label">{t("orderDetails.email")}</div>
                    <div>{ORDER_STATE.order.user_id.email}</div>
                  </div>
                  <div className="row">
                    <div className="label">{t("orderDetails.phoneNumber")}</div>
                    <div>{ORDER_STATE.order.user_id.phone}</div>
                  </div>
                  <div className="row">
                    <div className="label">{t("orderDetails.nifNumber")}</div>
                    <div>{isEmpty(ORDER_STATE.order.user_id.nif) ? "-" : ORDER_STATE.order.user_id.nif}</div>
                  </div>
                </div>
                ) : (
                <div className="col">
                  <div className="row">
                    <div className="label">{t("table.id")}</div>
                    <div>— Cliente removido</div>
                  </div>
                </div>
                )}
              </div>

              <div className="details">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid lightgrey", marginRight: "15px" }}>
                  <div className="title">{t("orderDetails.products")}</div>
                  <div>
                    {/* Order status should not be cancelled and subscription should be  */}
                    {!(ORDER_STATE?.order?.status === purchaseOrderStatus.cancelled) &&
                      (ORDER_STATE?.order?.user_id?.stripe_subscription_id ||
                        ORDER_STATE?.order?.user_id?.stripe_subscription_schedule_id ||
                        ORDER_STATE?.order?.stripe_subscription_id ||
                        ORDER_STATE?.order?.stripe_subscription_schedule_id) && (
                      <Button style={{ marginRight: "10px" }} startIcon={<Add />} color="primary" variant="contained" onClick={() => setIsOpenAddProductDialog(true)}>
                        {t("orderDetails.addNewProduct")}
                      </Button>
                    )}
                    <Button startIcon={<Add />} color="primary" variant="contained" onClick={() => onOpenItems(ORDER_STATE.order.products)}>
                      {t("orderDetails.addNewItem")}
                    </Button>
                    <Button style={{ marginLeft: "10px" }} color="default" variant="outlined" onClick={() => setIsExtendSubDialogOpen(true)}>
                      + Meses
                    </Button>
                  </div>
                </div>
                <div className="table">
                  <TableContainer component={Paper}>
                    <Table aria-label="collapsible table" size="small">
                      <TableHead>
                        <TableRow>
                          <StyledTableCell />
                          <StyledTableCell>{t("table.id")}</StyledTableCell>
                          <StyledTableCell align="center">{t("orders.type")}</StyledTableCell>
                          <StyledTableCell align="center">{t("orders.returned")}</StyledTableCell>
                          <StyledTableCell align="center">{t("orders.contentItems")}</StyledTableCell>
                          <StyledTableCell align="center">{t("orders.size", "Size (m²)")}</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {uniqBy(ORDER_STATE?.order?.products || [], '_id').map(row => (
                          <ProductLists
                            key={row._id}
                            row={row}
                            userPriceVersion={ORDER_STATE?.order?.price_version ?? ORDER_STATE?.order?.user_id?.price_version}
                            showIsReturnedColumn={true}
                            showItemDeleteColumn={ORDER_STATE.order.status === purchaseOrderStatus.processing || ORDER_STATE.order.status === purchaseOrderStatus.confirmed}
                            showItemEditColumn={true}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </div>
            </div>
          )}
        </div>
      )}


      {ORDER_STATE.order && (
        <div className="details" style={{ marginTop: "20px" }}>
          <div style={{ borderBottom: "1px solid lightgrey", marginRight: "15px", marginBottom: "12px", paddingBottom: "8px" }}>
            <span style={{ fontSize: "16px", fontWeight: "600" }}>Notas Internas</span>
            <span style={{ fontSize: "12px", color: "#888", marginLeft: "8px" }}>Visível apenas no backoffice</span>
          </div>
          <div style={{ marginRight: "15px" }}>
            {(ORDER_STATE.order.internal_notes || []).length === 0 && (
              <div style={{ color: "#aaa", fontSize: "13px", marginBottom: "12px" }}>Sem notas.</div>
            )}
            {(ORDER_STATE.order.internal_notes || []).map(note => (
              <div key={note._id} style={{ display: "flex", alignItems: "flex-start", background: "#fffbe6", border: "1px solid #ffe58f", borderRadius: "6px", padding: "10px 12px", marginBottom: "8px", gap: "10px" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "14px", color: "#333", marginBottom: "4px" }}>{note.text}</div>
                  <div style={{ fontSize: "11px", color: "#999" }}>
                    {note.author} · {note.created_at ? new Date(note.created_at).toLocaleString("pt-PT") : ""}
                  </div>
                </div>
                <IconButton size="small" disabled={internalNoteLoading} onClick={() => handleDeleteInternalNote(note._id)} style={{ color: "#999" }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
            <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
              <TextField
                multiline
                minRows={2}
                variant="outlined"
                size="small"
                placeholder="Adicionar nota interna..."
                value={internalNoteText}
                onChange={e => setInternalNoteText(e.target.value)}
                style={{ flex: 1 }}
              />
              <Button
                variant="contained"
                color="primary"
                disabled={internalNoteLoading || !internalNoteText.trim()}
                onClick={handleAddInternalNote}
                style={{ alignSelf: "flex-end", whiteSpace: "nowrap" }}
              >
                {internalNoteLoading ? "..." : "Guardar"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {ORDER_STATE.order && (
        <div className="details" style={{ marginTop: "20px", marginBottom: "20px" }}>
          <div style={{ borderBottom: "1px solid lightgrey", marginRight: "15px", marginBottom: "12px", paddingBottom: "8px", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: "16px", fontWeight: "600" }}>Histórico de alterações</span>
            {activityLoading && <span style={{ fontSize: "12px", color: "#aaa" }}>a carregar...</span>}
          </div>
          <div style={{ marginRight: "15px" }}>
            {activityError && !activityLoading && (
              <div style={{ color: "#b71c1c", fontSize: "13px" }}>Não foi possível carregar o histórico. Recarrega a página.</div>
            )}
            {activityLogs.length === 0 && !activityLoading && !activityError && (
              <div style={{ color: "#aaa", fontSize: "13px" }}>Sem registos de alterações.</div>
            )}
            {activityLogs.map((log, i) => {
              const isNote = log.action === "note";
              const isStatus = log.action === "status_changed";
              const dotColor = isNote ? "#ff9800" : isStatus ? "#1976d2" : "#9e9e9e";
              return (
                <div key={log._id || i} style={{ display: "flex", gap: 12, marginBottom: 10, alignItems: "flex-start" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 3 }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: dotColor, flexShrink: 0 }} />
                    {i < activityLogs.length - 1 && (
                      <div style={{ width: 1, height: "100%", minHeight: 20, backgroundColor: "#e0e0e0", marginTop: 3 }} />
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: "#333" }}>{log.description}</div>
                    <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>
                      {log.actor_name || "Sistema"} · {log.timestamp ? new Date(log.timestamp).toLocaleString("pt-PT") : ""}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

            <CustomerAppView open={customerViewOpen} onClose={() => setCustomerViewOpen(false)} order={ORDER_STATE.order} />

      <Dialog open={signatureDialogOpen} onClose={() => setSignatureDialogOpen(false)} maxWidth="lg">
        <DialogContent style={{ position: "relative", padding: 0 }}>
          <IconButton
            onClick={() => setSignatureDialogOpen(false)}
            style={{ position: "absolute", right: 8, top: 8, backgroundColor: "rgba(255, 255, 255, 0.9)", zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>
          {ORDER_STATE.order?.signature_image_url && (
            <img src={ORDER_STATE.order.signature_image_url} alt={t("orderDetails.signatureAlt", "Customer signature")} style={{ width: "100%", display: "block" }} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderDetails;




